import { getShortDisplayName, isUnnamedFirstName } from "./name.jsx";
import { formatDashboardFamilyLineHeadName } from "./dashboardMemberDisplayName";
import { isDashboardStartOrTopOfLineMember } from "./dashboardStartOfLineMember";
import { getMemberDobTimestamp } from "./family-tree/dateAndTime";
import { blockFamilyTreeNavigationOnMobile } from "../Components/Shared/FeatureNoticeModal";

const START_TOP_OF_LINE_LABEL = "Start/Top of Line";

function normalizeColorKey(colorName) {
  if (!colorName) return "";
  return String(colorName)
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "");
}

/**
 * First name plus the suffix or special initial that distinguishes it — the legend
 * spells the start/top couple as "Joseph, Sr. & Frances". Records often carry the
 * middle name inside `first_name` ("Joseph Alexander"), so only its leading word
 * is kept; the rest of the short name comes from the same helper the Family
 * Line/Color column uses.
 *
 * `keepFullFirstName` opts out of that trimming for heads of line. Their label has
 * to match `formatDashboardFamilyLineHeadName`, which keeps the whole first name:
 * a line whose rows are present on the page is named from the rows and a line with
 * no rows falls back to here, so trimming only one of the two spelled the same line
 * "Mary M" on one list and "Mary" on another.
 */
function getMemberShortName(member, { keepFullFirstName = false } = {}) {
  const rawFirst = String(member?.first_name ?? member?.firstName ?? "").trim();
  const first =
    (keepFullFirstName ? rawFirst.replace(/\s+/g, " ") : rawFirst.split(/\s+/)[0]) || "";
  const special = String(
    member?.special_name ?? member?.specialName ?? ""
  ).trim();

  if (!first) return special;

  // An unnamed head ("UnNamed Boy") keeps the whole placeholder and the last
  // name, as formatDashboardFamilyLineHeadName spells it from the rows.
  const last = String(member?.last_name ?? member?.lastName ?? "").trim();
  if (isUnnamedFirstName(rawFirst) && last) {
    return getShortDisplayName(`${rawFirst.replace(/\s+/g, " ")} ${last}`, member?.suffix, special);
  }

  return getShortDisplayName(first, member?.suffix, special);
}

/**
 * Two heads can share a first name and be told apart only by the initial that
 * follows it — "Mary M" heads one line, "Mary F" another. The payload spells them
 * unevenly: a row's `family_line_*` fields can carry the bare first name while the
 * heads-of-line record (or the head's own member record) carries the initial, so
 * whichever source is read alone drops the "F" and both lines read "Mary".
 *
 * Where one spelling is the other plus a trailing token, the fuller one wins.
 * Spellings that are not related that way leave the caller's preferred source
 * standing, so this only ever adds the distinguishing initial back.
 */
export function pickRicherFamilyLineHeadName(preferred, alternate) {
  const primary = String(preferred ?? "").trim();
  const other = String(alternate ?? "").trim();

  if (!primary) return other;
  if (!other) return primary;
  if (primary === other) return primary;

  return other.toLowerCase().startsWith(`${primary.toLowerCase()} `)
    ? other
    : primary;
}

/** Oldest first; an unknown date of birth (-1) sorts last rather than first. */
function byOldestDobFirst(a, b) {
  if (a.dob === b.dob) return 0;
  if (a.dob === -1) return 1;
  if (b.dob === -1) return -1;
  return a.dob - b.dob;
}

export function extractHeadsOfLineFromStatisticsResponse(data) {
  const headsOfLineData =
    data?.heads_of_line?.details ||
    data?.family_lines?.details ||
    data?.heads_of_line ||
    data?.family_lines ||
    data?.headsOfLine ||
    [];

  return Array.isArray(headsOfLineData) ? headsOfLineData : [];
}

/**
 * Every member in the tree, from the same statistics payload each dashboard list
 * already fetches.
 *
 * The colour legend is built from this rather than from a page's own rows so it
 * reads identically on every list. Built from the rows, a line only appears where
 * that page happens to contain someone on it — the start/top couple's line has no
 * heads-of-line record to fall back on, so it vanished entirely from lists that
 * held none of its members.
 */
export function extractAllMembersFromStatisticsResponse(data) {
  const members =
    data?.total_family_members?.details || data?.total_family_members || [];

  return Array.isArray(members) ? members : [];
}

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

/** Leading word of the first name — "Mary Magdalene" and "Mary M" both key on "mary". */
function firstNameKey(record, memberRecord) {
  const first = String(
    record?.first_name ??
      record?.firstName ??
      memberRecord?.first_name ??
      memberRecord?.firstName ??
      ""
  ).trim();
  return first.split(/\s+/)[0].toLowerCase();
}

function suffixKey(record, memberRecord) {
  const raw = record?.suffix ?? record?.head_suffix ?? memberRecord?.suffix;
  const value = typeof raw === "object" && raw !== null ? raw.name : raw;
  return String(value ?? "")
    .trim()
    .replace(/\.$/, "")
    .toLowerCase();
}

/**
 * Middle initial, which is what tells two heads of the same first name apart:
 * Mary Magdalene heads one line, Mary Frances another, and the family reads them
 * as "Mary M" and "Mary F".
 */
function middleInitial(record, memberRecord) {
  const middle = String(
    record?.middle_name ??
      record?.middleName ??
      memberRecord?.middle_name ??
      memberRecord?.middleName ??
      ""
  ).trim();
  return middle ? middle[0].toUpperCase() : "";
}

/** "Mary M" already carries its initial; "Mary" and "Roger, Sr." do not. */
function endsWithInitial(name) {
  return /\s[A-Za-z]\.?$/.test(String(name || "").trim());
}

/**
 * Name each head of line, adding the middle initial only where it does work: two
 * heads share a first name and the same suffix, so the initial is the one thing
 * that separates them. A head whose first name is theirs alone keeps it plain —
 * "Joseph Alexander, Sr." stays "Joseph, Sr." rather than becoming "Joseph A".
 */
function buildHeadDisplayNames(headsOfLine, memberById) {
  const names = new Map();
  const byNameKey = new Map();

  for (const head of headsOfLine) {
    const id = recordId(head);
    if (!id) continue;

    const memberRecord = memberById.get(id);
    const key = `${firstNameKey(head, memberRecord)}|${suffixKey(
      head,
      memberRecord
    )}`;
    if (!byNameKey.has(key)) byNameKey.set(key, []);
    byNameKey.get(key).push({ head, memberRecord, id });
  }

  byNameKey.forEach((entries) => {
    entries.forEach(({ head, memberRecord, id }) => {
      const baseName = pickRicherFamilyLineHeadName(
        getMemberShortName(head, { keepFullFirstName: true }),
        memberRecord
          ? getMemberShortName(memberRecord, { keepFullFirstName: true })
          : ""
      );
      if (!baseName) return;

      const initial =
        entries.length > 1 && !endsWithInitial(baseName)
          ? middleInitial(head, memberRecord)
          : "";

      names.set(id, initial ? `${baseName} ${initial}` : baseName);
    });
  });

  return names;
}

/**
 * @param {Array<object>} headsOfLine the tree's heads-of-line block
 * @param {Array<object>} [members] the tree's full roster, which carries the middle
 *   names the heads block can leave out — see `buildHeadDisplayNames`
 */
export function buildHeadsOfLineIdByColorMap(headsOfLine = [], members = []) {
  const byColor = new Map();
  const byHex = new Map();
  const headByColor = new Map();
  const headByHex = new Map();
  const headNameByColor = new Map();
  const headNameByHex = new Map();
  const headNameById = new Map();

  const empty = {
    byColor,
    byHex,
    headByColor,
    headByHex,
    headNameByColor,
    headNameByHex,
    headNameById,
    all: [],
  };
  if (!Array.isArray(headsOfLine)) return empty;

  const memberById = new Map();
  (Array.isArray(members) ? members : []).forEach((member) => {
    const id = recordId(member);
    if (id) memberById.set(id, member);
  });

  buildHeadDisplayNames(headsOfLine, memberById).forEach((name, id) =>
    headNameById.set(id, name)
  );

  for (const head of headsOfLine) {
    const id = head?.id ?? head?.member_id;
    if (!id) continue;

    const colorKey = normalizeColorKey(
      head?.color_name || head?.family_line_color
    );
    const hexKey = String(
      head?.hex_code || head?.family_line_hex_code || ""
    ).toLowerCase();
    const displayName = headNameById.get(String(id)) || "";

    if (colorKey) {
      byColor.set(colorKey, id);
      headByColor.set(colorKey, head);
      // First head wins the colour, matching headByColor above; a line headed by a
      // couple is named from both in the legend rather than from this map.
      if (displayName && !headNameByColor.has(colorKey)) {
        headNameByColor.set(colorKey, displayName);
      }
    }
    if (hexKey) {
      byHex.set(hexKey, id);
      headByHex.set(hexKey, head);
      if (displayName && !headNameByHex.has(hexKey)) {
        headNameByHex.set(hexKey, displayName);
      }
    }
  }

  // `all` keeps every head record, not just one per colour, so the legend can list
  // the tree's full set of lines and name couples who head a line together.
  return {
    byColor,
    byHex,
    headByColor,
    headByHex,
    headNameByColor,
    headNameByHex,
    headNameById,
    all: headsOfLine,
  };
}

/** The head's display name for a member's line, via the colour/hex maps. */
export function resolveDashboardFamilyLineHeadName(member, headsMap) {
  if (!member || !headsMap) return "";

  const colorKey = normalizeColorKey(
    member.color_name || member.family_line_color || ""
  );
  const hexKey = String(
    member.hex_code || member.family_line_hex_code || ""
  ).toLowerCase();

  return (
    (colorKey && headsMap.headNameByColor?.get(colorKey)) ||
    (hexKey && headsMap.headNameByHex?.get(hexKey)) ||
    ""
  );
}

/** Resolve head-of-line record for a member via color/hex maps. */
export function resolveDashboardFamilyLineHead(member, headsMap) {
  if (!member || !headsMap) return null;

  const colorKey = normalizeColorKey(
    member.color_name || member.family_line_color || ""
  );
  const hexKey = String(
    member.hex_code || member.family_line_hex_code || ""
  ).toLowerCase();

  if (colorKey && headsMap.headByColor?.has(colorKey)) {
    return headsMap.headByColor.get(colorKey);
  }
  if (hexKey && headsMap.headByHex?.has(hexKey)) {
    return headsMap.headByHex.get(hexKey);
  }
  return null;
}

/**
 * Short head name for Family Line/Color: first + suffix/special (no last),
 * preferring the heads-of-line record when available.
 */
export function getDashboardFamilyLineShortHeadName(member, headsMap) {
  const fromRow = formatDashboardFamilyLineHeadName(member);
  const fromHeadsMap = resolveDashboardFamilyLineHeadName(member, headsMap);
  if (fromHeadsMap) return pickRicherFamilyLineHeadName(fromHeadsMap, fromRow);

  const head = resolveDashboardFamilyLineHead(member, headsMap);
  if (head) {
    const fromHead = getShortDisplayName(
      head.first_name ?? head.firstName,
      head.suffix ?? head.head_suffix,
      head.special_name ?? head.specialName
    );
    if (fromHead) return pickRicherFamilyLineHeadName(fromHead, fromRow);
  }
  return fromRow;
}

/**
 * Head name for the Family Line/Color column: the row's own `family_line_*` fields,
 * which is what that column has always shown, restored to the head's full spelling
 * when the heads-of-line block knows the initial the row omits ("Mary" → "Mary F").
 * Rows whose line has no head record — the start/top of line — are untouched.
 */
export function getDashboardFamilyLineHeadDisplayName(member, headsMap) {
  const fromRow = formatDashboardFamilyLineHeadName(member);
  const fromHeadsMap = resolveDashboardFamilyLineHeadName(member, headsMap);
  if (fromHeadsMap) return pickRicherFamilyLineHeadName(fromRow, fromHeadsMap);

  const head = resolveDashboardFamilyLineHead(member, headsMap);
  if (!head) return fromRow;

  return pickRicherFamilyLineHeadName(
    fromRow,
    getMemberShortName(head, { keepFullFirstName: true })
  );
}

export function getDashboardFamilyLineNavigationMemberId(member, headsMap) {
  if (!member || typeof member !== "object") return null;

  const directId =
    member.head_id ??
    member.head_member_id ??
    member.head_of_line_id ??
    member.head_of_line?.head_id ??
    member.head_member?.id;
  if (directId) return directId;

  if (isDashboardStartOrTopOfLineMember(member)) {
    return member.id ?? member.member_id ?? null;
  }

  const colorName = member.color_name || member.family_line_color || "";
  const hexCode = member.hex_code || member.family_line_hex_code || "";
  const colorKey = normalizeColorKey(colorName);
  const hexKey = String(hexCode).toLowerCase();

  if (headsMap) {
    if (colorKey && headsMap.byColor?.has(colorKey)) {
      return headsMap.byColor.get(colorKey);
    }
    if (hexKey && headsMap.byHex?.has(hexKey)) {
      return headsMap.byHex.get(hexKey);
    }
  }

  const memberFirst = String(member.first_name || member.firstName || "")
    .trim()
    .toLowerCase();
  const headFirst = String(
    member.head_first_name || member.family_line_first_name || ""
  )
    .trim()
    .toLowerCase();
  if (memberFirst && headFirst && memberFirst === headFirst) {
    return member.id ?? member.member_id ?? null;
  }

  return member.id ?? member.member_id ?? null;
}

export function navigateToDashboardFamilyLineHead(navigate, member, headsMap) {
  // Phones get the "tree is being revised" notice instead of the unusable canvas
  if (blockFamilyTreeNavigationOnMobile()) return;
  const memberId = getDashboardFamilyLineNavigationMemberId(member, headsMap);
  if (memberId) {
    navigate(`/family-tree?memberId=${memberId}`);
  }
}

/**
 * Order the footer colour legend the way the family reads: the start/top of line
 * always first, then the remaining lines oldest to youngest by their head's date
 * of birth. Lines with no usable date at all fall to the end, alphabetically,
 * rather than jumping to the front.
 */
function compareDashboardColorLegendLines(a, b) {
  if (a.isStartTopOfLine !== b.isStartTopOfLine) {
    return a.isStartTopOfLine ? -1 : 1;
  }

  if (a.headDob !== b.headDob) {
    // -1 is the "no date of birth" sentinel, not an early timestamp
    if (a.headDob === -1) return 1;
    if (b.headDob === -1) return -1;
    return a.headDob - b.headDob;
  }

  return a.headName.localeCompare(b.headName);
}

/**
 * Every family line the report can speak about, gathered per colour + hex.
 *
 * The heads-of-line block covers the whole tree, so it seeds the map and the legend
 * keeps listing a line even when the active filter leaves none of its people on
 * screen. The visible rows then fill in the names and dates the block does not
 * carry, and add any line the block omits altogether.
 */
function collectDashboardLegendLines(members, headsMap) {
  const byLine = new Map();

  const lineEntry = (colorName, hexCode) => {
    const key = `${normalizeColorKey(colorName)}|${String(
      hexCode
    ).toLowerCase()}`;
    let entry = byLine.get(key);
    if (!entry) {
      entry = {
        colorName,
        hexCode,
        headNames: [], // from the heads-of-line block
        headDisplayNames: [], // those names with the middle initial that separates
        headIds: new Set(), // ids of those heads, to find their own rows below
        headMemberName: "", // head name as spelled on the head's own row
        rootNames: [], // tree-root people, who head their line themselves
        rowHeadName: "", // head name as spelled on the rows
        oldestRowDob: -1,
        isStartTopOfLine: false,
      };
      byLine.set(key, entry);
    }
    return entry;
  };

  const addName = (list, shortName, dob) => {
    if (!shortName) return;
    if (list.some((entry) => entry.shortName === shortName)) return;
    list.push({ shortName, dob });
  };

  (Array.isArray(headsMap?.all) ? headsMap.all : []).forEach((head) => {
    const colorName = head?.color_name || head?.family_line_color;
    if (!colorName) return;

    const entry = lineEntry(
      colorName,
      head?.hex_code || head?.family_line_hex_code || "#000000"
    );
    addName(
      entry.headNames,
      getMemberShortName(head, { keepFullFirstName: true }),
      getMemberDobTimestamp(head)
    );

    const headId = head?.id ?? head?.member_id;
    if (headId != null && headId !== "") {
      entry.headIds.add(String(headId));
      const displayName = headsMap?.headNameById?.get(String(headId));
      if (displayName && !entry.headDisplayNames.includes(displayName)) {
        entry.headDisplayNames.push(displayName);
      }
    }
  });

  (Array.isArray(members) ? members : []).forEach((member) => {
    const colorName = member?.family_line_color || member?.color_name;
    if (!colorName) return;

    const entry = lineEntry(
      colorName,
      member?.family_line_hex_code || member?.hex_code || "#000000"
    );

    const rowHeadName = formatDashboardFamilyLineHeadName(member);
    if (rowHeadName && !entry.rowHeadName) {
      entry.rowHeadName = rowHeadName;
    }

    // The head's own row is the third spelling of the line's name, and the one the
    // list prints beside their photo. It carries the initial that tells two heads
    // with the same first name apart even when the line fields on the other rows
    // leave it off.
    const memberId = member?.id ?? member?.member_id;
    if (memberId != null && entry.headIds.has(String(memberId))) {
      entry.headMemberName = pickRicherFamilyLineHeadName(
        entry.headMemberName,
        getMemberShortName(member, { keepFullFirstName: true })
      );
    }

    const dob = getMemberDobTimestamp(member);
    if (dob !== -1 && (entry.oldestRowDob === -1 || dob < entry.oldestRowDob)) {
      entry.oldestRowDob = dob;
    }

    // The tree root carries no head-of-line name of its own, so the root people
    // name their line themselves: "Joseph, Sr. & Frances".
    if (!rowHeadName && isDashboardStartOrTopOfLineMember(member)) {
      entry.isStartTopOfLine = true;
      addName(entry.rootNames, getMemberShortName(member), dob);
    }
  });

  return byLine;
}

/**
 * One entry per family line in the tree, for the compact footer legend, in
 * oldest-to-youngest order starting with the top of line.
 *
 * Every line is listed on every report — a line the current filter has emptied
 * still holds its place in the key. The dashboard endpoints spell the line colour
 * two ways depending on the section (family_line_* on the grouped pages, plain
 * color_name/hex_code elsewhere), so both are read here.
 *
 * @param {Array<object>} members rows currently visible on the page
 * @param {object} headsMap from buildHeadsOfLineIdByColorMap — the tree's full line set
 */
export function buildDashboardColorLegendLines(members, headsMap) {
  const lines = [];

  collectDashboardLegendLines(members, headsMap).forEach((entry) => {
    const rootNames = [...entry.rootNames].sort(byOldestDobFirst);
    const headNames = [...entry.headNames].sort(byOldestDobFirst);

    // The rows are the first authority on a line's name because that is what the
    // column beside them shows; the heads block names the lines they leave out.
    // Where a single head's other spellings only add the initial the rows drop,
    // that fuller spelling wins — otherwise "Mary F" and "Mary M" both read "Mary".
    let headName =
      entry.rowHeadName ||
      rootNames.map((name) => name.shortName).join(" & ") ||
      headNames.map((name) => name.shortName).join(" & ") ||
      START_TOP_OF_LINE_LABEL;

    if (headName !== START_TOP_OF_LINE_LABEL) {
      headName = pickRicherFamilyLineHeadName(headName, entry.headMemberName);
      if (headNames.length === 1) {
        headName = pickRicherFamilyLineHeadName(headName, headNames[0].shortName);
      }
      // A line headed by one person is named the way the heads map spells them,
      // initial and all; a couple keeps the "A & B" spelling built above.
      if (entry.headDisplayNames.length === 1) {
        headName = pickRicherFamilyLineHeadName(
          headName,
          entry.headDisplayNames[0]
        );
      }
    }

    // A line's age is its head's. Where the heads block is silent, the oldest
    // person on the line stands in for it, so the order still reads oldest first.
    const headDob =
      headNames.find((name) => name.dob !== -1)?.dob ??
      rootNames.find((name) => name.dob !== -1)?.dob ??
      entry.oldestRowDob;

    lines.push({
      headName,
      colorName: entry.colorName,
      hexCode: entry.hexCode,
      isStartTopOfLine: entry.isStartTopOfLine,
      headDob,
    });
  });

  return lines.sort(compareDashboardColorLegendLines);
}
